<template>
  <div class="file-upload-component">
    <div
      v-if="drop"
      class="upload-drop-area"
      :class="{ 'drag-over': isDragging }"
      @drop.prevent="onDrop"
      @dragover.prevent="isDragging = true"
      @dragleave.prevent="isDragging = false"
      @click="selectFiles"
    >
      <input
        ref="fileInput"
        type="file"
        :multiple="multiple"
        :accept="accept"
        @change="onFileChange"
        style="display: none"
      />
      <slot>
        <button type="button" class="btn btn-primary">
          Select Files
        </button>
      </slot>
    </div>
    <div v-else @click="selectFiles">
      <input
        ref="fileInput"
        type="file"
        :multiple="multiple"
        :accept="accept"
        @change="onFileChange"
        style="display: none"
      />
      <slot>
        <button type="button" class="btn btn-primary">
          Select Files
        </button>
      </slot>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, nextTick } from 'vue'

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => []
  },
  postAction: {
    type: String,
    required: true
  },
  multiple: {
    type: Boolean,
    default: false
  },
  directory: {
    type: Boolean,
    default: false
  },
  dropDirectory: {
    type: Boolean,
    default: false
  },
  drop: {
    type: Boolean,
    default: false
  },
  addIndex: {
    type: Boolean,
    default: false
  },
  accept: {
    type: String,
    default: ''
  },
  headers: {
    type: Object,
    default: () => ({})
  },
  data: {
    type: Object,
    default: () => ({})
  },
  extensions: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['update:modelValue', 'input-filter', 'input-file'])

const fileInput = ref(null)
const files = ref([...props.modelValue])
const isDragging = ref(false)
const activeUploads = ref(new Set())
let fileIdCounter = 0

// Watch for external changes to modelValue
watch(() => props.modelValue, (newVal) => {
  if (newVal !== files.value) {
    files.value = [...newVal]
  }
})

// Watch for active property changes
const activeWatcher = ref(false)
watch(activeWatcher, (shouldActivate) => {
  if (shouldActivate) {
    uploadAllFiles()
    activeWatcher.value = false
  }
})

const selectFiles = () => {
  fileInput.value?.click()
}

const onFileChange = (event) => {
  const selectedFiles = Array.from(event.target.files)
  addFiles(selectedFiles)
  event.target.value = '' // Reset input
}

const onDrop = async (event) => {
  isDragging.value = false
  const items = event.dataTransfer.items
  const droppedFiles = event.dataTransfer.files

  if (items && props.dropDirectory) {
    await processDroppedItems(items)
  } else if (droppedFiles && droppedFiles.length > 0) {
    const fileArray = Array.from(droppedFiles)
    addFiles(fileArray)
  }
}

const processDroppedItems = async (items) => {
  const fileList = []
  const itemsArray = Array.from(items)
  const promises = []

  for (const item of itemsArray) {
    if (item.kind === 'file') {
      const entry = item.webkitGetAsEntry?.()
      if (entry) {
        promises.push(traverseFileTree(entry, fileList))
      } else {
        const file = item.getAsFile()
        if (file) {
          fileList.push(file)
        }
      }
    }
  }

  await Promise.all(promises)
  addFiles(fileList)
}

const traverseFileTree = async (entry, fileList, path = '') => {
  if (entry.isFile) {
    return new Promise((resolve) => {
      entry.file((file) => {
        fileList.push(file)
        resolve()
      })
    })
  } else if (entry.isDirectory) {
    const dirReader = entry.createReader()

    // Read all entries by calling readEntries repeatedly
    // (browsers limit each call to ~100 entries)
    const readAllEntries = async () => {
      const allEntries = []

      const readBatch = () => {
        return new Promise((resolve) => {
          dirReader.readEntries((entries) => {
            if (entries.length === 0) {
              // No more entries, we're done
              resolve()
            } else {
              // Add this batch and read next batch
              allEntries.push(...entries)
              readBatch().then(resolve)
            }
          })
        })
      }

      await readBatch()
      return allEntries
    }

    const entries = await readAllEntries()

    // Process all entries recursively
    for (const entry of entries) {
      await traverseFileTree(entry, fileList, path + entry.name + '/')
    }
  }
}

const addFiles = (fileList) => {
  fileList.forEach((file, index) => {
    const fileObject = {
      id: ++fileIdCounter,
      name: file.name,
      size: file.size,
      type: file.type,
      file: file,
      progress: 0,
      success: false,
      error: '',
      active: false,
      blob: '',
      thumb: ''
    }

    // Create blob URL
    const URL = window.URL || window.webkitURL
    if (URL && URL.createObjectURL) {
      fileObject.blob = URL.createObjectURL(file)
      if (file.type.startsWith('image/')) {
        fileObject.thumb = fileObject.blob
      }
    }

    if (props.addIndex) {
      fileObject.index = files.value.length + index
    }

    // Emit input-filter event for validation
    let prevented = false
    const prevent = () => { prevented = true }

    emit('input-filter', fileObject, null, prevent)

    if (!prevented && validateFile(fileObject)) {
      files.value.push(fileObject)
      emit('update:modelValue', files.value)
      emit('input-file', fileObject, null)
    }
  })
}

const validateFile = (file) => {
  // Extension validation
  if (props.extensions) {
    const allowedExtensions = props.extensions.split('|').filter(e => e)
    const fileExtension = file.name.split('.').pop().toLowerCase()
    if (!allowedExtensions.includes(fileExtension)) {
      file.error = `File type .${fileExtension} not allowed`
      return false
    }
  }

  return true
}

const uploadFile = async (file) => {
  if (activeUploads.value.has(file.id)) {
    return // Already uploading
  }

  activeUploads.value.add(file.id)

  const oldFile = { ...file }

  const formData = new FormData()
  formData.append('file', file.file)

  // Append additional data
  Object.keys(props.data).forEach((key) => {
    const value = props.data[key]
    if (value !== null && value !== undefined) {
      if (typeof value === 'object') {
        formData.append(key, JSON.stringify(value))
      } else {
        formData.append(key, value)
      }
    }
  })

  try {
    // Use XMLHttpRequest for progress tracking
    const xhr = new XMLHttpRequest()

    // Setup progress tracking
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) {
        const progress = Math.round((e.loaded * 100) / e.total)
        updateFileInArray(file, { progress })
        emit('input-file', file, oldFile)
      }
    })

    // Setup completion handler
    const uploadPromise = new Promise((resolve, reject) => {
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText)
            resolve(response)
          } catch {
            resolve(xhr.responseText)
          }
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`))
        }
      })

      xhr.addEventListener('error', () => {
        reject(new Error('Network error occurred'))
      })

      xhr.addEventListener('abort', () => {
        reject(new Error('Upload aborted'))
      })
    })

    // Set headers and credentials
    xhr.open('POST', props.postAction)

    // Enable credentials for cookie-based authentication
    xhr.withCredentials = true

    // Set custom headers (skip Authorization if empty/undefined)
    Object.keys(props.headers).forEach((key) => {
      const value = props.headers[key]
      if (value && value !== 'Bearer ' && value !== 'Bearer undefined') {
        xhr.setRequestHeader(key, value)
      }
    })

    // Send request
    xhr.send(formData)

    // Wait for completion
    const responseData = await uploadPromise

    updateFileInArray(file, {
      success: true,
      progress: 100,
      active: false,
      response: responseData
    })
    emit('input-file', file, oldFile)
  } catch (error) {
    const errorMessage = error.message || 'Upload failed'
    updateFileInArray(file, {
      error: errorMessage,
      active: false
    })
    emit('input-file', file, oldFile)
  } finally {
    activeUploads.value.delete(file.id)
  }
}

const updateFileInArray = (file, data) => {
  const fileIndex = files.value.findIndex((f) => f.id === file.id)
  if (fileIndex > -1) {
    Object.assign(files.value[fileIndex], data)
    Object.assign(file, data)
  }
}

const removeFile = (file) => {
  const index = files.value.findIndex((f) => f.id === file.id || f === file)
  if (index > -1) {
    const oldFile = files.value[index]
    if (oldFile.blob) {
      URL.revokeObjectURL(oldFile.blob)
    }
    files.value.splice(index, 1)
    emit('input-file', null, oldFile)
    emit('update:modelValue', files.value)
  }
}

const update = (fileOrId, data) => {
  let file
  if (typeof fileOrId === 'object') {
    file = fileOrId
  } else {
    file = files.value.find((f) => f.id === fileOrId)
  }

  if (file) {
    const oldFile = { ...file }
    updateFileInArray(file, data)

    // If active is set to true, start upload
    if (data.active === true && !file.success && !file.error) {
      nextTick(() => {
        uploadFile(file)
      })
    }

    emit('input-file', file, oldFile)
  }
}

const remove = (file) => {
  removeFile(file)
}

const uploadAllFiles = () => {
  files.value.forEach(file => {
    if (!file.success && !file.error && file.progress === 0) {
      uploadFile(file)
    }
  })
}

// Features object for browser capability detection
const features = {
  directory: true,
  html5: true,
  drop: true
}

// Expose methods and properties for parent component access via ref
defineExpose({
  update,
  remove,
  files,
  features,
  get active() {
    return activeWatcher.value
  },
  set active(value) {
    activeWatcher.value = value
  },
  get $el() {
    return fileInput.value?.parentElement
  }
})
</script>

<style scoped>
.file-upload-component {
  display: inline-block;
  width: auto;
  max-width: 100%;
}

.upload-drop-area {
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 1.25rem;
  display: inline-block;
  width: auto;
}

.upload-drop-area.drag-over {
  background-color: rgba(240, 185, 59, 0.2);
  transform: scale(1.02);
  box-shadow: 0 0 20px rgba(240, 185, 59, 0.5);
}
</style>
